import JsSIP from "jssip";
import adapter from "webrtc-adapter";

// Initialize adapter
adapter.browserDetails.browser;

// Audio elements
const ringing = new Audio(require("@/assets/sounds/ringing.mp3"));
const calling = new Audio(require("@/assets/sounds/calling.mp3"));
ringing.loop = true;
calling.loop = true;

class WebRTCService {
  constructor() {
    this.coolPhone = null;
    this.session = null;
    this.socket = null;
    this.pcStream = null;
    this.icomingProFulfiled = false;
    this.auto_answer = { enabled: false, timeout: 3000 };
    this.auto_answer_timeout = null;
    this.keepAliveInterval = null; // Add keep-alive timer
    this.options = {
      mediaConstraints: { audio: true, video: false },
      pcConfig: {
        iceServers: [],
        iceTransportPolicy: "all",
      },
    };
    this.callbacks = {
      onConnected: null,
      onDisconnected: null,
      onRegistered: null,
      onUnregistered: null,
      onRegistrationFailed: null,
      onNewSession: null,
      onSessionEnded: null,
      onSessionAccepted: null,
      onSessionFailed: null,
      onSessionProgress: null,
      onSessionHold: null,
      onSessionUnhold: null,
      onSessionMuted: null,
      onSessionUnmuted: null,
      onSessionConfirmed: null,
      onPeerConnection: null,
      onSessionIncoming: null,
      onSessionOutgoing: null,
      onIncomingStream: null,
      onOutgoingStream: null,
      onPeerConnectionTrack: null,
      onConnectionLost: null, // Add callback for connection loss during visibility changes
    };

    // Preferred microphone (audio input). Persisted so it survives restarts and
    // is reused for subsequent calls (via mediaConstraints); switchAudioInput()
    // also hot-swaps it into the *live* call. currentInputStream holds the
    // stream we switched in so we can release it when the call ends (jssip only
    // stops the track it created itself).
    this.currentInputStream = null;
    this.preferredInputDeviceId = null;
    this.loadPreferredInput();
    this.applyPreferredInput();

    // Add page visibility and focus event handlers
    this.setupPageEventHandlers();
  }

  // Set callbacks for event handling
  setCallbacks(callbacks) {
    this.callbacks = { ...this.callbacks, ...callbacks };
  }

 // Initialize phone configuration
  initPhoneConfig(phoneInfo) {
    if (phoneInfo.stunURI && phoneInfo.stunURI !== "") {
      this.options.pcConfig.iceServers = [
        // { urls: `stun:${phoneInfo.stunURI}`},
        {
          urls: `turn:${phoneInfo.stunURI}?transport=tcp`,
          username: phoneInfo.turnUser,
          credential: phoneInfo.turnPass,
        },
      ];
    }
    
    // Set auto-answer configuration from phoneInfo
    this.setAutoAnswerConfig(phoneInfo.isAutoAnswer);
  }


  // Set auto-answer configuration
  setAutoAnswerConfig(isAutoAnswer) {
    this.auto_answer = {
      enabled: isAutoAnswer || false,
      timeout: 3000, // Hardcoded timeout
    };
    console.log('Auto-answer configuration updated:', this.auto_answer);
  }

  // Create and start SIP session
  createSession(phoneInfo) {
    // Clean up existing UA if it exists to prevent multiple instances
    if (this.coolPhone) {
      console.log('Cleaning up existing UA before creating new session');
      try {
        this.coolPhone.stop();
      } catch (error) {
        console.log('Error stopping existing UA:', error);
      }
      this.coolPhone = null;
    }
    
    const connectURL = this.buildConnectURL(phoneInfo);
    this.socket = new JsSIP.WebSocketInterface(connectURL);
    
    const configuration = {
      sockets: [this.socket],
      uri: `sip:${phoneInfo.exten}@${phoneInfo.pbxServer}`,
      password: phoneInfo.secret,
    };

    this.coolPhone = new JsSIP.UA(configuration);
    this.setupPhoneEventHandlers();
    this.coolPhone.start();
  }

  // Build WebSocket connection URL
  buildConnectURL(phoneInfo) {
    let connectURL = `wss://${phoneInfo.pbxServer}`;
    if (phoneInfo.pbxPort && phoneInfo.pbxPort !== "") {
      connectURL += `:${phoneInfo.pbxPort}`;
    }
    return `${connectURL}/ws`;
  }

  // Setup phone event handlers
  setupPhoneEventHandlers() {
    this.coolPhone.on("connected", () => {
      console.log('WebSocket connected to SIP server');
      if (this.callbacks.onConnected) {
        this.callbacks.onConnected();
      }
      // Start keep-alive when connected
      this.startKeepAlive();
    });

    this.coolPhone.on("disconnected", (e) => {
      console.log('WebSocket disconnected from SIP server');
      if (this.callbacks.onDisconnected) {
        this.callbacks.onDisconnected(e);
      }
      // Stop keep-alive when disconnected
      this.stopKeepAlive();
    });

    this.coolPhone.on("newRTCSession", (data) => {
      this.handleNewSession(data);
    });

    this.coolPhone.on("registered", () => {
      console.log('SIP registration successful');
      if (this.callbacks.onRegistered) {
        this.callbacks.onRegistered();
      }
    });

    this.coolPhone.on("unregistered", () => {
      console.log('SIP unregistered');
      if (this.callbacks.onUnregistered) {
        this.callbacks.onUnregistered();
      }
    });

    this.coolPhone.on("registrationFailed", (e) => {
      console.log('SIP registration failed:', e);
      if (this.callbacks.onRegistrationFailed) {
        this.callbacks.onRegistrationFailed(e);
      }
    });
  }

  // Add keep-alive methods
  startKeepAlive() {
    this.stopKeepAlive(); // Clear any existing interval
    
    this.keepAliveInterval = setInterval(() => {
      if (this.socket && this.socket._ws && this.socket._ws.readyState === WebSocket.OPEN) {
        console.log('Sending WebRTC keep-alive ping');
        // Send a harmless OPTIONS request to keep connection alive
        try {
          this.socket._ws.send('\r\n\r\n'); // Send empty keep-alive
        } catch (error) {
          console.log('Keep-alive failed:', error);
          this.stopKeepAlive();
        }
      } else {
        console.log('WebSocket not available for keep-alive');
        this.stopKeepAlive();
      }
    }, 25000); // Send keep-alive every 25 seconds
  }

  stopKeepAlive() {
    if (this.keepAliveInterval) {
      clearInterval(this.keepAliveInterval);
      this.keepAliveInterval = null;
    }
  }

  // Handle new RTC session
  handleNewSession(data) {
    if (this.session) {
      this.session = null;
    }
    this.session = data.session;
    this.icomingProFulfiled = false; // Reset flag for new session
    this.setupSessionEventHandlers();
    
    if (this.callbacks.onNewSession) {
      this.callbacks.onNewSession(this.session);
    }
  }

  // Setup session event handlers
  setupSessionEventHandlers() {
    this.session.on("ended", (e) => {
      if (this.callbacks.onSessionEnded) {
        this.callbacks.onSessionEnded(e);
      }
      this.pauseRingtone();
      this.stopCurrentInputStream(); // release a switched-in mic device
      clearTimeout(this.auto_answer_timeout);
    });

    this.session.on("connecting", (e) => {
      console.log("session connecting", e);
    });

    this.session.on("sending", (e) => {
      console.log("session sending", e);
    });

    this.session.on("progress", (e) => {
      console.log("session progress", e);
      if (this.callbacks.onSessionProgress) {
        this.callbacks.onSessionProgress(e);
      }
    });

    this.session.on("hold", (e) => {
      console.log("session hold", e);
      if (this.callbacks.onSessionHold) {
        this.callbacks.onSessionHold(e);
      }
    });

    this.session.on("unhold", (e) => {
      console.log("session unhold", e);
      if (this.callbacks.onSessionUnhold) {
        this.callbacks.onSessionUnhold(e);
      }
    });

    this.session.on("muted", (e) => {
      console.log("session muted", e);
      if (this.callbacks.onSessionMuted) {
        this.callbacks.onSessionMuted(e);
      }
    });

    this.session.on("unmuted", (e) => {
      console.log("session unmuted", e);
      if (this.callbacks.onSessionUnmuted) {
        this.callbacks.onSessionUnmuted(e);
      }
    });

    this.session.on("failed", (e) => {
      console.log("session failed", e);
      if (this.callbacks.onSessionFailed) {
        this.callbacks.onSessionFailed(e);
      }
      this.pauseRingtone();
      this.stopCurrentInputStream(); // release a switched-in mic device
    });

    this.session.on("accepted", (e) => {
      console.log("session accepted", e);
      if (this.callbacks.onSessionAccepted) {
        this.callbacks.onSessionAccepted(e);
      }
      this.pauseRingtone();
    });

    this.session.on("confirmed", (e) => {
      console.log("session confirmed", e);
      if (this.callbacks.onSessionConfirmed) {
        this.callbacks.onSessionConfirmed(e);
      }
    });

    this.session.on("peerconnection", (e) => {
      this.handlePeerConnection(e);
    });

    this.setupIncomingOutgoingHandlers();
  }

  // Setup incoming/outgoing call handlers
  setupIncomingOutgoingHandlers() {
    console.log('Setting up handlers for session direction:', this.session.direction);
    if (this.session.direction === "incoming") {
      console.log('Handling as incoming call');
      this.handleIncomingCall();
    } else {
      console.log('Handling as outgoing call');
      this.handleOutgoingCall();
    }
  }

  // Handle incoming call
  handleIncomingCall() {
    console.log('handleIncomingCall called');
    if (this.callbacks.onSessionIncoming) {
      this.callbacks.onSessionIncoming();
    }

    ringing.play().catch((error) => {
      console.log("error in incoming play ringing error", error);
    });

    if (this.session.connection) {
      console.log('Setting up addstream listener for incoming call');
      this.session.connection.addEventListener("addstream", (e) => {
        console.log('addstream event fired for incoming call', e);
        console.log('addstream stream:', e.stream);
        this.handleIncomingStream(e);
      });
    }

    // Auto-answer for incoming calls based on configuration
    if (this.auto_answer.enabled) {
      console.log(`Auto-answer enabled, will answer in ${this.auto_answer.timeout}ms`);
      this.auto_answer_timeout = setTimeout(
        () => this.session.answer(this.options),
        this.auto_answer.timeout
      );
    }
  }

  // Handle outgoing call
  handleOutgoingCall() {
    if (this.callbacks.onSessionOutgoing) {
      this.callbacks.onSessionOutgoing();
    }

    this.session.connection.addEventListener("addstream", (e) => {
      this.handleOutgoingStream(e);
    });
  }

  // Handle incoming stream
  handleIncomingStream(e) {
    if (this.callbacks.onIncomingStream) {
      this.callbacks.onIncomingStream(e);
    }
  }

  // Handle outgoing stream
  handleOutgoingStream(e) {
    if (this.callbacks.onOutgoingStream) {
      this.callbacks.onOutgoingStream(e);
    }
  }

  // Handle peer connection
  handlePeerConnection(e) {
    console.log('handlePeerConnection called', e);
    const peerconnection = e.peerconnection;

    peerconnection.ontrack = (e) => {
      console.log('ontrack event fired', e);
      console.log('ontrack streams:', e.streams);
      console.log('ontrack track:', e.track);
      
      // Use the first stream from streams array
      if (e.streams && e.streams.length > 0) {
        this.pcStream = e.streams[0];
        console.log('Using stream from streams array:', this.pcStream);
      } else if (e.stream) {
        this.pcStream = e.stream;
        console.log('Using stream from stream property:', this.pcStream);
      }
      
      if (this.callbacks.onPeerConnectionTrack) {
        this.callbacks.onPeerConnectionTrack(e);
      }
    };

    if (this.callbacks.onPeerConnection) {
      this.callbacks.onPeerConnection(e);
    }
  }

  // Call methods
  call(number, pbxServer) {
    return this.coolPhone.call(`sip:${number}@${pbxServer}`, this.options);
  }

  answer() {
    if (this.session && !this.session.isEstablished()) {
      this.session.answer(this.options);
      this.answerIncoming();
    }
  }

  answerIncoming() {
    console.log('answerIncoming called');
    if (this.session.connection != null) {
      console.log('Setting up addstream listener for answer');
      this.session.connection.addEventListener("addstream", (e) => {
        console.log('addstream event fired for answer', e);
        console.log('addstream stream in answer:', e.stream);
        if (this.callbacks.onIncomingStream) {
          this.callbacks.onIncomingStream(e);
        }
      });
    }
  }

  hangup() {
    if (this.session) {
      // Check session state before attempting termination
      const sessionStatus = this.session._status;
      const validTerminationStates = [1, 2, 3, 4, 5, 6, 9]; // States where termination is valid
      
      console.log('Hangup called - session status:', sessionStatus);
      
      if (validTerminationStates.includes(sessionStatus)) {
        try {
          this.session.terminate();
          console.log('Session terminated via hangup');
        } catch (error) {
          console.log('Error during hangup termination:', error);
        }
      } else {
        console.log('Session already terminated or in invalid state, hangup skipped');
      }
    } else {
      console.log('No active session to hangup');
    }
  }

  hold() {
    if (this.session && !this.session.isOnHold().local) {
      this.session.hold();
    } else if (this.session && this.session.isOnHold().local) {
      this.session.unhold();
    }
  }

  mute() {
    if (this.session && !this.session.isMuted().audio) {
      this.session.mute({ audio: true });
    } else if (this.session && this.session.isMuted().audio) {
      this.session.unmute({ audio: true });
    }
  }

  sendDTMF(tone) {
    if (this.session) {
      this.session.sendDTMF(tone);
    }
  }

  // ---- Audio input (microphone) device selection ------------------------

  // List available microphones. Device labels are only populated once mic
  // permission has been granted (i.e. during/after a call) — which is exactly
  // when this picker is used.
  async getAudioInputDevices() {
    if (!navigator.mediaDevices || !navigator.mediaDevices.enumerateDevices) {
      return [];
    }
    const devices = await navigator.mediaDevices.enumerateDevices();
    return devices.filter((d) => d.kind === "audioinput");
  }

  // The device id actually feeding the active call (read off the live sender
  // track), falling back to the persisted preference when there is no call yet.
  getCurrentInputDeviceId() {
    const pc = this.session && this.session.connection;
    if (pc) {
      const sender = pc
        .getSenders()
        .find((s) => s.track && s.track.kind === "audio");
      if (sender && sender.track && sender.track.getSettings) {
        const id = sender.track.getSettings().deviceId;
        if (id) return id;
      }
    }
    return this.preferredInputDeviceId;
  }

  // Switch the microphone mid-call: grab a fresh track from the chosen device
  // and hot-swap it into the peer connection via replaceTrack (no SDP
  // renegotiation). This is what makes a headset plugged in AFTER answering
  // usable without redialing. The choice is also persisted for later calls.
  async switchAudioInput(deviceId) {
    const pc = this.session && this.session.connection;
    if (!pc) {
      throw new Error("No active call to switch the microphone on");
    }

    const sender = pc
      .getSenders()
      .find((s) => s.track && s.track.kind === "audio");
    if (!sender) {
      throw new Error("No audio track on the active call");
    }

    const newStream = await navigator.mediaDevices.getUserMedia({
      audio: deviceId ? { deviceId: { exact: deviceId } } : true,
      video: false,
    });
    const newTrack = newStream.getAudioTracks()[0];
    if (!newTrack) {
      newStream.getTracks().forEach((t) => t.stop());
      throw new Error("Selected microphone produced no audio track");
    }

    // A fresh track defaults to enabled; if the call is currently muted, carry
    // that over so switching devices doesn't silently un-mute the agent.
    // (jssip mute/unmute toggles track.enabled on the sender, so it keeps
    // working on the replacement track — see _toggleMuteAudio.)
    if (this.session.isMuted && this.session.isMuted().audio) {
      newTrack.enabled = false;
    }

    const oldTrack = sender.track;
    await sender.replaceTrack(newTrack);
    if (oldTrack && oldTrack !== newTrack) {
      oldTrack.stop(); // release the previous device
    }

    // Release any earlier switched-in stream we owned (keeping the now-live
    // track), then hold the new one so we can stop it when the call ends.
    this.stopCurrentInputStream(newTrack);
    this.currentInputStream = newStream;

    this.preferredInputDeviceId =
      newTrack.getSettings().deviceId || deviceId || null;
    this.applyPreferredInput();
    this.savePreferredInput();

    return this.preferredInputDeviceId;
  }

  // Apply the preferred device to the constraints jssip uses when it calls
  // getUserMedia for future calls. `ideal` (not `exact`) so a missing device
  // falls back to the OS default instead of failing the whole call.
  applyPreferredInput() {
    if (this.preferredInputDeviceId) {
      this.options.mediaConstraints.audio = {
        deviceId: { ideal: this.preferredInputDeviceId },
      };
    } else {
      this.options.mediaConstraints.audio = true;
    }
  }

  loadPreferredInput() {
    try {
      this.preferredInputDeviceId =
        localStorage.getItem("preferredAudioInput") || null;
    } catch (e) {
      this.preferredInputDeviceId = null;
    }
  }

  savePreferredInput() {
    try {
      if (this.preferredInputDeviceId) {
        localStorage.setItem(
          "preferredAudioInput",
          this.preferredInputDeviceId
        );
      } else {
        localStorage.removeItem("preferredAudioInput");
      }
    } catch (e) {
      /* localStorage unavailable — non-fatal */
    }
  }

  // Stop the mic stream we switched in, optionally sparing one track (the one
  // still live on the call after a replaceTrack).
  stopCurrentInputStream(keepTrack) {
    if (this.currentInputStream) {
      this.currentInputStream.getTracks().forEach((t) => {
        if (t !== keepTrack) t.stop();
      });
      this.currentInputStream = null;
    }
  }

  // Audio control
  pauseRingtone() {
    ringing.pause();
    calling.pause();
    this.icomingProFulfiled = false;
  }

  // Connection management
  isConnected() {
    // Only return true if both WebSocket is connected AND SIP is registered
    return this.coolPhone && this.coolPhone.isConnected() && this.coolPhone.isRegistered();
  }

  isRegistered() {
    return this.coolPhone && this.coolPhone.isRegistered();
  }

  // Check if WebSocket is connected (without SIP registration)
  isWebSocketConnected() {
    return this.coolPhone && this.coolPhone.isConnected();
  }

  stop() {
    console.log('Stopping WebRTC phone...');
    if (this.coolPhone) {
      this.coolPhone.stop();
      this.coolPhone = null;
    }
  }

  unregister() {
    console.log('Unregistering WebRTC phone...');
    if (this.coolPhone && this.coolPhone.isRegistered()) {
      this.coolPhone.unregister();
    }
  }

  disconnect() {
    console.log('Disconnecting WebRTC socket...');
    if (this.socket) {
      this.socket.disconnect();
      this.socket = null;
    }
  }

  // Comprehensive cleanup method
  cleanup() {
    console.log('Performing comprehensive WebRTC cleanup...');
    
    // Clear any pending timeouts
    if (this.auto_answer_timeout) {
      clearTimeout(this.auto_answer_timeout);
      this.auto_answer_timeout = null;
    }
    
    // Stop keep-alive mechanism
    this.stopKeepAlive();
    
    // Remove page event handlers
    this.removePageEventHandlers();
    
    // Pause audio
    this.pauseRingtone();

    // Release any microphone stream we switched in during the call
    this.stopCurrentInputStream();

    // Terminate active session
    if (this.session) {
      console.log('Terminating active session...');
      
      // Check if session is in a valid state to be terminated
      // JSSIP session statuses: 0=NULL, 1=INVITE_SENT, 2=1XX_RECEIVED, 3=INVITE_RECEIVED, 
      // 4=WAITING_FOR_ANSWER, 5=ANSWERED, 6=WAITING_FOR_ACK, 7=CANCELED, 8=TERMINATED, 9=CONFIRMED
      const sessionStatus = this.session._status;
      const validTerminationStates = [1, 2, 3, 4, 5, 6, 9]; // States where termination is valid
      
      console.log('Session status:', sessionStatus);
      
      if (validTerminationStates.includes(sessionStatus)) {
        try {
          this.session.terminate();
          console.log('Session terminated successfully');
        } catch (error) {
          console.log('Error terminating session:', error);
        }
      } else {
        console.log('Session already terminated or in invalid state, skipping termination');
      }
      
      this.session = null;
    }
    
    // Clear stream references
    this.pcStream = null;
    this.icomingProFulfiled = false;
    
    // Unregister and disconnect
    this.unregister();
    this.disconnect();
    this.stop();
    
    console.log('WebRTC cleanup completed');
  }

  // Specific cleanup for logout - removes everything including global references
  logoutCleanup() {
    console.log('Performing WebRTC logout cleanup...');
    
    // Do comprehensive cleanup first
    this.cleanup();
    
    // Remove global reference
    if (typeof window !== 'undefined' && window.WebRTCService === this) {
      delete window.WebRTCService;
    }
    
    // Reset all callbacks
    Object.keys(this.callbacks).forEach(key => {
      this.callbacks[key] = null;
    });
    
    console.log('WebRTC logout cleanup completed');
  }

  // Reset service state
  reset() {
    console.log('Resetting WebRTC service state...');
    this.coolPhone = null;
    this.session = null;
    this.socket = null;
    this.pcStream = null;
    this.icomingProFulfiled = false;
    this.auto_answer_timeout = null;
    this.currentInputStream = null;
    console.log('WebRTC service state reset');
  }

  // Getters
  getSession() {
    return this.session;
  }

  getSessionPeer() {
    if (this.session) {
      return this.session.remote_identity.uri.user;
    }
    return "";
  }

  getSessionDirection() {
    return this.session ? this.session.direction : null;
  }

  getSessionStartTime() {
    return this.session ? this.session._start_time : null;
  }

  // Setup page visibility and focus event handlers
  setupPageEventHandlers() {
    // Store bound handlers for later removal
    this.boundHandlers = {
      visibilityChange: () => {
        if (document.hidden) {
          // console.log('Page hidden - handling tab inactivity');
          this.handleTabHidden();
        } else {
          // console.log('Page visible - handling tab activation');
          this.handleTabVisible();
        }
      },
      windowFocus: () => {
        // console.log('Window focused - resuming audio and checking connection');
        this.handleWindowFocus();
      },
      windowBlur: () => {
        // console.log('Window blurred - handling tab inactivity');
        this.handleWindowBlur();
      }
    };

    // Handle page visibility changes
    document.addEventListener('visibilitychange', this.boundHandlers.visibilityChange);

    // Handle window focus/blur events
    window.addEventListener('focus', this.boundHandlers.windowFocus);
    window.addEventListener('blur', this.boundHandlers.windowBlur);
  }

  // Remove page event handlers
  removePageEventHandlers() {
    if (this.boundHandlers) {
      document.removeEventListener('visibilitychange', this.boundHandlers.visibilityChange);
      window.removeEventListener('focus', this.boundHandlers.windowFocus);
      window.removeEventListener('blur', this.boundHandlers.windowBlur);
      this.boundHandlers = null;
    }
  }

  // Handle when tab becomes hidden
  handleTabHidden() {
    // Pause ringtone to save resources
    this.pauseRingtone();
    
    // Note: Don't disconnect WebRTC - just pause audio
    // console.log('Tab hidden - audio paused, WebRTC connection maintained');
  }

  // Handle when tab becomes visible
  handleTabVisible() {
    // console.log('Tab visible - checking connection health');
    
    // Resume audio if there's an active session
    if (this.session && this.session.isEstablished()) {
      this.resumeAudioStream();
    }
    
    // Force connection health check
    this.checkConnectionHealth();
    
    // If not connected, attempt immediate reconnection
    if (!this.isConnected()) {
      // console.log('Tab visible but not connected - forcing reconnection');
      // Give a callback to trigger phone reinit from the main component
      if (this.callbacks.onConnectionLost) {
        this.callbacks.onConnectionLost();
      }
    }
    
    // console.log('Tab visible - audio resumed, connection checked');
  }

  // Handle window focus
  handleWindowFocus() {
    // console.log('Window focused - performing comprehensive check');
    
    // Resume audio streams
    this.resumeAudioStream();
    
    // Check connection status
    const isConnected = this.isConnected();
    const isRegistered = this.isRegistered();
    
    // console.log('Focus check:', { isConnected, isRegistered });
    
    // If not connected, trigger reconnection
    if (!isConnected) {
      // console.log('Window focused but not connected - triggering reconnection');
      if (this.callbacks.onConnectionLost) {
        this.callbacks.onConnectionLost();
      }
      return;
    }
    
    // If connected but not registered, attempt registration
    if (isConnected && !isRegistered) {
      // console.log('Window focused - attempting re-registration');
      this.coolPhone.register();
    }
    
    // Restart keep-alive to ensure fresh connection
    this.startKeepAlive();
  }

  // Handle window blur
  handleWindowBlur() {
    // Don't disconnect - just pause audio to save resources
    // console.log('Window blurred - maintaining connection');
  }

  // Resume audio stream when tab becomes active
  resumeAudioStream() {
    // console.log('Attempting to resume audio stream');
    
    // First, try to resume the global audio context if suspended
    this.resumeAudioContext();
    
    const audioElement = document.getElementById('remote-stream-audio');
    if (audioElement && audioElement.srcObject && audioElement.paused) {
      // console.log('Resuming audio stream');
      audioElement.play().catch((error) => {
        console.log('Error resuming audio stream:', error);
        // If autoplay failed, we might need user interaction
        this.handleAutoplayError(audioElement);
      });
    }
    
    // Also check if we have an active session without audio
    if (this.session && this.session.isEstablished() && !audioElement?.srcObject) {
      // console.log('Active session without audio stream - attempting to restore');
      this.restoreSessionAudio();
    }
  }

  // Resume audio context if suspended
  resumeAudioContext() {
    try {
      // Get the global audio context if available
      const AudioContext = window.AudioContext || window.webkitAudioContext;
      if (AudioContext) {
        const audioContext = new AudioContext();
        if (audioContext.state === 'suspended') {
          // console.log('Audio context suspended - attempting to resume');
          audioContext.resume().then(() => {
            // console.log('Audio context resumed successfully');
          }).catch((error) => {
            console.log('Failed to resume audio context:', error);
          });
        }
      }
    } catch (error) {
      console.log('Error handling audio context:', error);
    }
  }

  // Handle autoplay errors by showing user notification
  handleAutoplayError(audioElement) {
    console.log('Autoplay blocked - audio requires user interaction');
    // Create a click handler to resume audio
    const resumeAudio = () => {
      audioElement.play().then(() => {
        console.log('Audio resumed after user interaction');
        document.removeEventListener('click', resumeAudio);
      }).catch((error) => {
        console.log('Audio still failed after user interaction:', error);
      });
    };
    
    // Add temporary click listener
    document.addEventListener('click', resumeAudio, { once: true });
  }

  // Restore audio for active session
  restoreSessionAudio() {
    if (!this.session || !this.session.connection) {
      return;
    }
    
    console.log('Attempting to restore session audio');
    
    // Re-attach audio stream if available
    if (this.pcStream) {
      const audioElement = document.getElementById('remote-stream-audio');
      if (audioElement) {
        audioElement.srcObject = this.pcStream;
        audioElement.play().catch((error) => {
          console.log('Error restoring session audio:', error);
        });
      }
    }
  }

  // Check connection health
  checkConnectionHealth() {
    if (this.coolPhone) {
      const isConnected = this.coolPhone.isConnected();
      const isRegistered = this.coolPhone.isRegistered();
      
      console.log('Connection health check:', { isConnected, isRegistered });
      
      if (!isConnected) {
        console.log('WebRTC not connected - attempting reconnection');
        // The connection will be handled by the main app
      }
    }
  }
}

const webRTCServiceInstance = new WebRTCService();

// Expose globally for connection synchronization
if (typeof window !== 'undefined') {
  window.WebRTCService = webRTCServiceInstance;
}

export default webRTCServiceInstance; 